import type { Layout } from "grid-layout-plus"
import type { DateOnly } from "./classes"
import type { JobStatuses, DataGapUpdateType, TaskSourceType, ActivitySourceType, DataGapStatus, DataGapReason, ManualTurnoverType, GroupingDimension, InsightsOperation, JobType, InsightsChartTypes, AuditAction, Permission, ContractLineType, MinimumFeeType, ContractTurnoverReportSource, UtilityTypes, BaseFeeTypes, MinimumFeeFrequency, ContractRuleType, AllowedIpType, PushApiKeyType, ContactRelationKinds } from "./enums"

interface BaseModel {
  id: number
  createdOn: string
  modifiedOn?: string
}

export interface GlobalState {
  serviceMode?: boolean
  openDataGapCount?: number
}

export interface User extends BaseModel {
  name: string
  email: string
  lastLogin?: string
  permissionSetId: number
  permissionSet: PermissionSet
  isSuperAdmin: boolean
}

export interface UserDetail extends User {
  permissions: string[]
}

export interface Company extends BaseModel {
  name: string
  legalEntityIdentifier: string
  centers: Center[]
}

export interface Center extends BaseModel {
  name: string
  streetAddress?: string
  zipCode?: string
  region?: string
  country: string
  timeZone: string
  currency: string
  companyId: number
  companyName: string
  locations: Location[]
}

export interface PermissionSet extends BaseModel {
  name: string
  description?: string
  color?: string
  textColor?: string
  isDefaultSet: boolean
  isSystemSet: boolean
  userCount?: number
  permissions: Permission[]
  externalReference?: string
}

export type PermissionGroup = {
  name: string
  requiredFeature?: string
  permissions: PermissionOption[]
}

export type PermissionOption = {
  description: string
  value: Permission,
  dependencies?: Permission[]
}

export type Alert = {
  id: number
  createdOn: string
  title: string
  typeName: string
  configurationId: number
  command: Command
  approved: true
  modifiedOn?: string
  respondedOn?: string
  shownOn?: string
  finishedOn?: string
  finishedById?: number
  finishedByName?: string
  status?: string
  description?: string
  referenceId?: string
  response?: string
}

export interface AuditLog {
  id: number
  userId: number
  userName: string
  entityName: string
  action: AuditAction
  primaryKey: string
  label: string
  changedOn: string
  changedColumns: string[]
}

export interface AuditLogDetail extends Omit<AuditLog, 'changedColumns'> {
  changes: {
    column: string
    type: string
    oldValue: unknown
    newValue: unknown
  }[]
}

export interface Command<Type = unknown> {
  id: number
  commandKey: string
  createdOn: string
  title: string
  description: string
  tenantName: string
  commandDataType: 'ManualTurnoverReportingCommandData' | 'DataGapCommandData' | 'AuditCommandData' | string
  commandData: Type
  commandInvalidMessage?: string
  expiresOn?: string
  executedOn?: string
}

export interface CommandData {
  commandAlreadyExecutedMessage: string
  successMessage: string
  attemptsUsedMessage: string
  expiredMessage: string
  canceledMessage: string
}

export interface ManualTurnoverReportingContract {
  contractNumber: string
  fileContent?: string | null
  fileName?: string | null
  savedFilePath?: string | null
  periods: ManualTurnoverReportingContractPeriod[]
}

export interface ManualTurnoverReportingContractPeriod {
  periodType: string
  periodKey: string
  periodDescription: string
  periodDatesDescription: string
  periodStart: string
  periodEnd: string
  ruleResponses: ManualTurnoverReportingContractPeriodRuleResponse[]
}

export interface ManualTurnoverReportingContractPeriodRuleResponse {
  ruleNumber: string
  ruleDescription: string
  turnover: number | null
  transactions: number | null
  quantity: number | null
  reportTurnover: boolean
  reportTransactions: boolean
  reportQuantity: boolean
}

export interface ManualTurnoverReportingCommandData extends CommandData {
  contracts: ManualTurnoverReportingContract[]
  receiptEmailTemplate: string
  reportedByName: string
}

export interface AuditCommandData extends ManualTurnoverReportingCommandData {}

export interface Contract extends BaseModel {
  name: string
  number: string
  start: string
  end: string
  contractTurnoverReportsEnabledOn: string
  turnoverReportsAutoApprovedOn: string
  invoicingEnabledOn?: string
  invoicingAutoApprovedOn?: string
  shops: {
    id: number
    name: string
    groupId: number
    groupName: string
    shopContractId: number
    openingOn?: string
    closingOn?: string
  }[]
  rules: ContractRule[]
}

export interface LinkContract extends Contract {
  isOverlapping: boolean
  isDifferentGroup: boolean
}

export interface ContractRule {
  id: number
  validFrom?: string
  validTo?: string
  isActive: true
  ruleKey?: string
  name?: string
  customDescription?: string
  comment?: string
  type: ContractRuleType
  reportTurnover: boolean
  reportTransactions: boolean
  reportQuantity: boolean
}

export interface ShopContract {
  id: number
  contract: Contract
  shop: Shop
}

export type InvoiceParameter = ContractBaseFee | ContractConcessionFee | ContractMinimumFee | ContractPrepayment | ContractSettlement

export interface ContractBaseFee extends BaseModel {
  contractId: number
  amount: number
  quantity?: number | null
  type: BaseFeeTypes
  utilityType?: UtilityTypes | null
  unit?: string | null
  text?: string | null
  validFrom: string
  validTo?: string
  excludeFromMinimumFees: boolean
}

export interface ContractPrepayment extends BaseModel {
  contractId: number
  amount: number
  start: string
  validFrom: string
  validTo?: string
  note?: string
}

export interface ContractMinimumFee extends BaseModel {
  contractId: number
  type: MinimumFeeType
  frequency: MinimumFeeFrequency
  amount: number
  paxLocationId?: number
  paxLocationName?: string
  paxTypeId?: number
  paxTypeName?: string
  validFrom: string
  validTo: string
}

export interface ContractConcessionFee extends BaseModel {
  contractRuleId: number
  contractRuleKey: string
  type: string
  criteriaFrom?: number
  criteriaTo?: number
  fee: number
  validFrom: string
  validTo: string
}

export interface ContractSettlement extends BaseModel {
  contractId: number
  frequency: string
  start: string
  profile: string
  validFrom: string
  validTo?: string
  note?: string
}

export interface ContractInvoiceLine extends BaseModel {
  contractId: number
  contractName: string
  contractNumber: string
  contractInvoiceId: number
  contractInvoicingPendingCalculationOn: string | null
  contractInvoicingLastCalculatedOn: string | null
  type: ContractLineType
  text?: string
  unitPrice: number
  quantity: number
  total: number
  periodFrom: string
  periodTo: string
}

export interface ContractInvoice extends BaseModel {
  contractId: number
  contractName: string
  contractNumber: string
  invoiceKey: number
  total: number
  lineTypes: string[]
  publishedOn?: string
  periodFrom: string
  periodTo: string
}

export interface ContractInvoiceDetail extends ContractInvoice {
  lines: ContractInvoiceLine[]
}

export type ContractInvoiceDraft = Pick<ContractInvoiceDetail, 'contractId' | 'contractName' | 'contractNumber' | 'total' | 'lineTypes' | 'lines'>

export interface PaxLocation extends BaseModel {
  paxLocationKey: string
  name: string
}

export interface PaxType extends BaseModel {
  paxTypeKey: string
  name: string
  description?: string
}

export interface Group extends BaseModel {
  name: string
}

export interface GroupDetail extends BaseModel {
  name: string
  statusId: number
  status: string
  actionPhase: string
  statusExpectedFinish: string
  integratorId: number
  integratorName: string
  posSystemId: number
  posSystemName: string
  datasourceId: number
  datasourceName: string
  customerNumber?: string
  comment: string
  redirectDataGapEmails: boolean
  isIntegrated: boolean
  contractTurnoverReportExportBufferInDays: number
  pushTurnoverTolerance?: number
  autoExportApprovedOn?: string
  manualTurnoverWeekly: boolean
  manualTurnoverMonthly: boolean
  /** Months in which reconsolidation will run. 1 = January. */
  reconsolidationMonths: number[] //
  shops: ShopDetail[]
  dataVerifiedOn?: string
  integrationTimeZoneInfoId?: string
  implementationStrategyDescription?: string
  implementationExpectedStart?: string
  implementationExpectedEnd?: string
  implementationExpectedInTest?: string
  importers?: any[]
  ftpsIpsForWhitelisting?: string
  ftpsIpsSentForWhitelistingOn?: string
  ftpsIpsWhitelistedOn?: string
  pushApiKey?: string
  pushIpsForWhitelisting?: string
  pushIpsSentForWhitelistingOn?: string
  pushIpsWhitelistedOn?: string
  mappingOutputs: GroupMappingOutput[]
}

export interface Shop extends BaseModel {
  name: string
  locationName?: string
  isClosed: boolean
  groupId: number
  groupName: string
}

export interface ShopDetail extends BaseModel {
  name: string
  groupId: number
  groupName: string
  locationId: number
  locationName?: string
  shopCategoryId: number
  shopCategoryName: string
  contracts: ShopContract[]
  dataGaps: DataGap[]
  dataIssues: boolean
  includedInReconsolidation: boolean
  integrationShopIds: string[]
  integrationPOSNumbers: string[]
  individualConsolidationPeriodFrom?: string
  individualConsolidationPeriodTo?: string
  isInIndividualConsolidationPhase: boolean
  integrationDataValidFrom?: string
  maxTransactionDateTime?: string
  minTransactionDateTime?: string
  openingOn?: string
  expectedDataApproval?: Date
  dataIssuesDescription?: string
  dataApprovalDescription?: string
  dataApprovedById?: number
  dataApprovedByName?: string
  dataApprovedOn?: string
  closingOn?: string
  squareMeters?: number
  isIntegrated: boolean
}

export interface ShopCategory extends BaseModel {
  name: string
  sortOrder: number
  shops: Shop[]
}

export interface LinkShopDetail extends ShopDetail {
  isOverlapping: boolean
  isDifferentGroup: boolean
}

export interface Importer extends BaseModel {
  typeName: string
  typeFullName: string
  groupId: number
  groupName: string
  active: boolean
  lastSync: string
  averageDurationInSeconds: number
  maxTransactionDate: string
}

export interface ImporterDetail extends Importer {
  lastImport_StartTime: string
  lastImport_ImportState: string
  lastImport_ProgressInPercent: number
  lastImport_DurationInSeconds: number
  lastImport_Message: string
  configuration: {
    active: boolean
    start: string
    executeTimeoutInSeconds: number
    expiresOn: string
    expireDescription: string
    values: [
      {
        name: string
        value: any
        type: string
        required: boolean
        description: string
        defaultValue: string
      }
    ]
  }
}

export interface ImporterType {
  text: string
  value: string
  importerTemplate: ImporterDetail
}

export interface Activity extends BaseModel {
  userId: number
  userName: string
  finishedByUserId?: number
  finishedByName: string
  activitySourceId: number
  activitySource: ActivitySourceType
  activitySourceValue: string
  message: string
  followUpOn?: string
  followUpBootstrapVariant: string
  dateGroup: string
  finishedOn?: string
}

export type ShopsMetadata = {
  actionPhases: {
    value: string
    text: string
    disabled: boolean
  }[]
  dataSources: {
    value: string
    text: string
    disabled: boolean
  }[]
  locations: {
    value: string
    text: string
    disabled: boolean
  }[]
  statuses: {
    value: string
    text: string
    disabled: boolean
  }[]
}

export interface GroupStatusEntry extends BaseModel {
  id: number
  groupId: number
  statusId: number
  statusName: string
  processStep: number
  expectedFinish?: string
  finishedOn?: string
}

export type Integrator = {
  id: number
  createdOn: string
  modifiedOn: string
  name: string
  description: string
  cphCreditorNumber: string
  groups: {
    id: number
    name: string
    createdOn: string
    modifiedOn: string
  }[]
}

export type ImporterJob = {
  startTime: string
  importState: string
  progressInPercent: number
  durationInSeconds: number
  message: string
}

export type Location = {
  id: number
  createdOn: string
  modifiedOn: string
  name: string
  centerId: number
  centerName: string
  sortOrder: number
  shops: Shop[]
}

export interface Contact extends BaseModel {
  name: string
  email: string
  phone: string
  description: string
  relationKind: ContactRelationKinds
  relationId: number
  relationName: string
}

export type ReportingDetailed = {
  groupName: string
  shopName: string
  integrationShopId: string
  saleUniqueId: string
  createdOn: string
  orderPayment: string
  transactionValue: number
  receiptNumber: string
  locationName: string
  transactionId: string
  salesLineUniqueId: string
  quantity: number
  price: number
  discount: number
  vat: number
  shopCategoryName?: string
  orderStart?: string
  orderReady?: string
  integrationPosNumber?: string
  salesChannel?: string
  system?: string
  paymentMethods?: string
  currencyCode?: string
  rebateTypes?: string
  service?: string
  flightId?: string
  airline?: string
  destination?: string
  taxCode?: string
  ean?: string
  itemCode?: string
  itemName?: string
  itemCategory1Code?: string
  itemCategory1Name?: string
  itemCategory2Code?: string
  itemCategory2Name?: string
  itemCategory3Code?: string
  itemCategory3Name?: string
  itemCategory4Code?: string
  itemCategory4Name?: string
  itemCategory5Code?: string
  itemCategory5Name?: string
  xSalesChannel?: string
  xDiscountType?: string
  xPaymentType?: string
  xContractRuleNumber?: string
  xProductClass?: string
  xProductBrand?: string
  deals?: string
  brand?: string
  productFamily?: string
  gender?: string
  size?: string
  packaging?: string
  certifications?: string
  diet?: string
  isAlcoholic?: boolean
  contractTurnoverGroup?: string
}

export interface Status extends BaseModel {
  name: string
  processStep: number
  expectedDurationInWeekDays: number
  groupTimestampFieldName?: string
  actionPhase: string
  enabled: boolean
  color: string
}

export type SalesQuery = {
  groupId: number
  fromDate: string
  toDate: string
  shopId?: number
}

export interface Log extends BaseModel {
  message: string
  userId: string
  logType: string
  sourceEntityId: number
  sourceEntityType: string
}

export interface Schedule extends BaseModel {
  job: number
  jobType: string
  active: boolean
  description: string
  frequency: number
  interval: number
  priority: number
  start: string
  statusString: string
  timeoutInSeconds: number
  nextRun: string
  lastRun: string
  lastRunStatus: true
}

export interface ConsolidationGroup {
  groupId: number;
  groupName: string;
  dataVerifiedOn?: string; // Date-time string in ISO 8601 format
  currentGroupStatusEntry_ExpectedFinish: string; // Date-time string in ISO 8601 format
  currentGroupStatusEntry_IsLate: boolean;
  actionPhase: string;
  status: string;
  integratorName: string;
  datasourceType: string;
  shops: ConsolidationShop[]
}

export interface ConsolidationShop {
  id: number
  nameWithLocation: string
  expectedDataApproval: string // Date-time string in ISO 8601 format
  dataApprovedOn?: string // Date-time string in ISO 8601 format
  dataApprovalDescription: string
  dataIssues: boolean
  dataIssuesDescription: string
  periods: ConsolidationPeriod[]
}

export interface ConsolidationPeriod {
  groupId: number;
  shopId: number;
  periodKey: string;
  periodStart: string; // Date-time string in ISO 8601 format
  periodEnd: string; // Date-time string in ISO 8601 format
  reportedBy: string;
  reportedOn: string; // Date-time string in ISO 8601 format
  integrationDays: number;
  expectedDays: number
  integrationDaysWarning: boolean;
  integrationContainsAdjustments: boolean;
  manualTurnoverContainsAdjustments: boolean;
  manualTurnoverId: number;
  turnoverManual: number;
  turnoverIntegration: number;
  turnoverDiff: number;
  turnoverDiffPct: number;
  turnoverWarning: boolean;
  transactionsManual: number;
  transactionsIntegration: number;
  transactionsDiff: number;
  transactionsDiffPct: number;
  transactionsWarning: boolean;
}

export interface PosSystem extends BaseModel {
  name: string;
  groups: Group[]
}

export interface ManualTurnover extends BaseModel {
  groupName: string
  shopNameWithLocation: string
  contractNumber: string
  type: ManualTurnoverType
  period: string
  periodKey: string
  ruleNumber: string
  ruleDescription: string
  reportedTurnover: number
  reportedTransactions: number
  reportedQuantity: number
  reportedBy: string
  reportedOn: string
  isAdjusted: boolean
  newTurnover: number
  newTransactions: number
  newQuantity: number
}

export interface Adjustment extends BaseModel {
  shopId: number
  contractRuleNumber: string
  shopNameWithLocation: string
  requestedBy: string
  requestedOn: string
  description: string
  adjustmentType: string
  date: string
  createdBy: string
  turnover: number
  transactions: number
}

export interface AlertRule extends BaseModel {
  title: string
  alertTypeName: string
  alertTypeFullName: string
  configurationJson: string
  active: boolean
  alertRecipientString: string
  alertRecipient: string
  configuration: {
    values: { 
      name: string
      type: string
      required: boolean
      label?: string
      value?: any
      description?: string
      defaultValue?: string
    }[]
  }
}

export interface MappingDetail {
  id: number
  groupId: number
  group: Group
  shopId?: number
  shop?: Shop
  validFrom: string
  validTo?: string
  priority: number
  output: MappingOutput
  ignore: boolean
  outputValue?: string
  conditions: Partial<MappingCondition>[]
  mappingSummary: string[]
  isVaild: boolean
}

export class MappingUpdateModel {
  constructor (mapping: MappingDetail) {
    this.id = mapping.id
    this.groupId = mapping.groupId
    this.shopId = mapping.shopId
    this.validFrom = mapping.validFrom
    this.validTo = mapping.validTo
    this.priority = mapping.priority
    this.outputId = mapping.output.id
    this.ignore = mapping.ignore
    this.outputValue = mapping.outputValue
    this.conditions = mapping.conditions.map(x => ({
      id: x.id,
      inputId: x.inputId,
      operator: x.operator,
      conditionValue: x.conditionValue
    }))
  }

  id: number
  groupId: number
  shopId?: number
  validFrom: string
  validTo?: string
  priority: number
  outputId: number
  ignore: boolean
  outputValue?: string
  conditions: {
    id?: number
    inputId?: number
    operator?: string
    conditionValue?: string
  }[]
}

export type MappingInput = {
  id: number
  name: string
  description: string
  label: string
  operators: MappingOperator[]
}

export type MappingOperator = {
  operator: string
  inputType: 'String' | 'StringArray' | 'Int' | 'DateTime'
}

export type MappingCondition = {
  id: number
  inputId: number
  input: MappingInput
  inputOptions?: string[]
  operator: string
  conditionValue: string
}

export type MappingOutput = {
  id: number
  name: string
  description: string
  allowFreeText: boolean
}

export type MappingOutputOption = {
  value: string
  description: string
  label: string
}

export type GroupMappingOutput = {
  groupId: number
  outputId: number
  outputName: string
  mappingEnabled: boolean
  mappingEnabledOn?: string
  note?: string
}

export type InputMappingPeriod = {
  start: string
  end?: string
}

export interface InputMappingModel {
  key: string
  label?: string
  inputId: number
  inputName: string
  inputValue: string | null
  outputValue: string | null
  children?: InputMappingModel[]
}

export interface Task {
  id: number
  name: string
  description?: string
  taskSourceId: number
  taskSourceType: TaskSourceType | ActivitySourceType
  userName: string
  actionDate: string
  createdOn: string
  numberOfShops?: number
  shopNames?: string
  taskDate: string
}

export interface Job {
  id: number
  createdOn: string
  type: JobType
  status: JobStatuses
  isActive: boolean
  isQueued: boolean
  parameters?: string
  postponeUntil?: string
  dependingOnId?: number
  startedOn?: string
  completedOn?: string
  failedCount: number
  lastFailedOn?: string
  log?: string
  relationId?: number
  relationKind?: string
  relationName?: string
  progressInPercent?: number
}

export interface JobStatus {
  activeCount: number
  queuedCount: number
}

export type ContractTurnoverReportListModel = {
  contractsNotApprovedForExport: TurnoverContract[]
  contractsApprovedForExport: TurnoverContract[]
  contractsExported: TurnoverContract[]
}

export type TurnoverContract = {
  groupId: number
  groupName: string
  contractId: number
  contractName: string
  contractNumber: string
  shopNames: string
  shopNamesWithLocation?: string[]
  contractTurnoverReportsAutoApprovedOn?: string
  contractTurnoverReportSource: ContractTurnoverReportSource
  approvedForExportOn?: string
  approvedForExportBy_UserId?: string
  approvedForExportBy_Name?: string
  exportedOn?: string
  reports: TurnoverContractReport[]
}

export type TurnoverContractReport = {
  id: number
  year: number
  month: number
  contractNumber: string
  contractStart: string
  contractEnd: string
  ruleNumber: string
  ruleDescription: string
  turnoverMonthMinus3: number
  turnoverMonthMinus2: number
  turnoverMonthMinus1: number
  turnover: number
  calculatedOn: string
  approvedForExportOn?: string
  approvedForExportBy_UserId?: string
  approvedForExportBy_Name?: string
  exportedOn?: string
  pendingCalculationOn?: string
  pendingCalculationSource?: string
}

export interface ContractTurnoverCalculation extends BaseModel {
  contractId: number
  contractName: string
  contractNumber: string
  contractRuleNumber: string
  year: number
  month: number
  previousTurnover: number
  newTurnover: number
  turnoverDifference: number
  source: string
  registeredOn: string
  groupId?: number
  groupName?: string
  exportedOn?: string
  archivedOn?: string
}

export type TimeZoneInfo = {
  id: string,
  hasIanaId: boolean,
  displayName: string,
  standardName: string,
  daylightName: string,
  baseUtcOffset: {
    ticks: number,
    days: number,
    hours: number,
    milliseconds: number,
    microseconds: number,
    nanoseconds: number,
    minutes: number,
    seconds: number,
    totalDays: number,
    totalHours: number,
    totalMilliseconds: number,
    totalMicroseconds: number,
    totalNanoseconds: number,
    totalMinutes: number,
    totalSeconds: number
  },
  supportsDaylightSavingTime: boolean
}

export interface DataGapIncident extends BaseModel {
  operatorId: number
  operatorName: string
  dataGaps: DataGap[]
}

export interface DataGap extends BaseModel {
  dataGapIncidentId: number
  shopId: number
  shopName: string
  locationName: string
  groupId: number
  groupName: string
  groupIntegrationType: string
  startDate: string
  endDate: string
  durationDays: number
  status: DataGapStatus | string
  reason?: DataGapReason | string
  resolvedOn?: string
  closedOn?: string
  lastEmailSentOn?: string
  updates: DataGapUpdate[]
  notes?: string
  manualHandling: boolean
  isInternalIssue: boolean
}

export type DataGapUpdate = {
  timestamp: string
  updateType: DataGapUpdateType | string
  description?: string
}

export interface InsightsGlobalFilter {
  fromDate: DateOnly
  toDate: DateOnly
  filters: Record<string, string[]>
}

export interface GlobalSelection {
  period: InsightsPeriod
  filters: Record<string, string[]>
}

export type InsightsPeriod = 'yesterday' | 'lastWeek' | 'lastMonth' | 'ytd' | string | [string, string]

export interface InsightsQuery {
  fromDate: DateOnly
  toDate: DateOnly
  filters: Record<string, string[]>
  sourceKey: string
  operation: InsightsOperation
  primaryGroupBy?: string
  secondaryGroupBy?: string
  hideEmptyGroupings?: boolean
}

export interface InsightsDashboard extends BaseModel {
  createdByUserId: number
  name: string
  description?: string
  isShared: boolean
  period?: string
  filters: Record<string, string[]>
  charts: InsightsChart[]
  isOwnView: boolean
  layout: Layout
}

export interface InsightsChart extends BaseModel {
  createdByUserId: number
  name: string
  description?: string
  period?: string
  sourceKey: string
  type: InsightsChartTypes
  operation: InsightsOperation
  groupBy: string[]
  filterEmptySeries: boolean
  flipAxes: boolean
  defaultWidth: number
  defaultHeight: number
  compare: boolean
  isShared: boolean
}

export interface InsightsFilter {
  key: string
  options: InsightsFilterOption[]
}

export interface InsightsFilterOption {
  key: string
  name: string
}

export interface InsightsGrouping {
  key: string
  sortOrder: number
  description?: string
}

export interface InsightsResponse {
  query: {
    metric: string
    operation: string
    fromDate: string
    toDate: string
    primaryGroupBy?: string
    secondaryGroupBy?: string
    filters?: unknown[]
  }
  data: InsightsDataPoint[]
}

export interface InsightsSource {
  name: string
  key: string
  groupings: InsightsSourceGrouping[]
}

export interface InsightsSourceGrouping {
  key: string
  dateFrom: string
  dateTo: string
}

export interface FilterItem {
  dimension: string
  options: InsightsFilterOption[]
  selectedKeys: string[]
}

export type InsightsDataPoint = {
  primaryGroupingKey: string | null
  primaryDimensionKey: string | null
  primaryDimensionName: string | null
  secondaryGroupingKey: string | null
  secondaryDimensionKey: string | null
  secondaryDimensionName: string | null
  value: number
}

export interface InsightsView extends BaseModel {
  userId: number
  name: string
  description?: string
  isShared: boolean
  isOwnView: boolean
  period?: InsightsPeriod
  filters: Record<string, string[]>
  turnoverChartMode: 'line' | 'bar'
  turnoverChartPrimaryGroupBy: GroupingDimension
  turnoverChartSecondaryGroupBy: GroupingDimension
}

export interface SalesChannel extends BaseModel {
  salesChannelKey: string
  name: string
  segmentKey: string
  segmentName: string
  sortOrder: number
  description: string
  fullName: string
}

export interface ShopSalesChannel extends BaseModel {
  salesChannelId: number
  shopId: number
  salesChannel: SalesChannel
  shop: Shop
}

export interface PushNode extends BaseModel {
  name: string
  url: string
  managementToken: string
  description?: string
  serviceModeEnabled: boolean // True when node is being patched
}

export interface Instruction extends BaseModel {
  type: string
  status: string
  startedOn: string
  completedOn: string
  content?: string
  log?: string
}

export interface ProductBrand extends BaseModel {
  name: string
  productBrandKey: string
}

export interface UtilityMeter extends BaseModel {
  utilityMeterKey: string
  type: UtilityTypes
  locationId: number
  locationReference?: string
}

export interface UtilityMeterContract extends BaseModel {
  utilityMeterId: number
  utilityMeterKey: string
  contractId: number
  contractName: string
  initialReading: number
  validFrom: string
  validTo?: string
}

export interface AllowedIp extends BaseModel {
  groupId: number
  groupName: string
  type: AllowedIpType
  address: string
  validFrom: string | null
  validTo: string | null
  enabled: boolean
  lastSeenOn: string | null
  contractValidFrom: string | null
  contractValidTo: string | null
}

export interface PushApiKey extends BaseModel {
  groupId: number
  prefix: string
  type: PushApiKeyType
  validFrom: string // DateOnly
  validTo: string // DateOnly
  lastSeenOn: string | null // DateTime
  revoked: boolean
  rotatedFromId: number | null
}

export type PushNodeStatus = 'healthy' | 'service' | 'unhealthy'
export type InsightsViewUpsertModel = Omit<InsightsView, 'id' | 'createdOn' | 'modifiedOn' | 'userId'>
export type OnboardingViewMode = 'normal' | 'compact' | 'detailed'
export type OnboadringSortBy = 'status' | 'shops'